import { Project } from './../types/Project';
import { useCallback, useEffect, useState } from 'react';
import { useMutate } from 'restful-react';
import { Encoder } from '../types/Encoder';
import { Stream } from '../types/Stream';
import isEqual from 'lodash.isequal';

const useAutosaveProject = (dataToSave: Project) => {
  const [project, setProject] = useState<Project>(dataToSave);
  const [current, setCurrent] = useState<Project>();
  const [debounceInterval, setDebounceInterval] = useState<number>(100);
  const [isAwaitingApiSaveToComplete, setIsAwaitingApiSaveToComplete] = useState<boolean>(false);
  const [isSavingEncoder, setIsSavingEncoder] = useState<boolean>(false);
  const [isSavingStream, setIsSavingStream] = useState<boolean>(false);

  const { mutate: postEncoder } = useMutate({
    verb: 'POST',
    path: `/projects/${project.id}/encoders/${project.encoder?.id}`,
  });

  const { mutate: postStream } = useMutate({
    verb: 'POST',
    path: `/projects/${project.id}/stream`,
  });

  const onSave = (data: Stream) => {
    if (data) {
      const newProject = {
        ...project,
        stream: data,
        encoder: data.encoders?.find((x) => x.id === project.encoder?.id) || ({} as Encoder)
      };
  
      if (!isEqual(newProject, project)) {
        setProject(newProject);
      }
    }
   
    setIsAwaitingApiSaveToComplete(false);
  };

  const updateEncoder = (encoder: Encoder, properties: Record<string, string | number | boolean>, debounce?: number | undefined) => {
    if (isSavingEncoder) {
      return;
    }

    let newEncoder = { ...encoder } as Encoder;

    for (const key in properties) {
      newEncoder = { ...newEncoder, [key]: properties[key] } as Encoder;
    }

    setIsAwaitingApiSaveToComplete(true);

    postEncoder(newEncoder).then((response) => {
      if (response.result) {
        onSave(response.result);
        setIsSavingEncoder(false);
      }
    }).catch((error) => {
      setIsSavingEncoder(false);
      setIsAwaitingApiSaveToComplete(false);
      console.log(`${JSON.stringify(error)}`);
    });
  };

  const updateStream = (properties: Record<string, string | number | boolean>, debounce: number | undefined) => {
    if (project.id === '') {
      return;
    }

    let stream = { ...project.stream } as Stream;

    for (const key in properties) {
      if (key === 'languageIsoCode') {
        stream.sourceLanguageIsoCode = properties[key].toString() ?? 'en-us';
        break;
      }

      if (key === 'audioDevice') {
        stream.audioDevice = properties[key].toString() ?? 'None:0'; 
        break;
      }
    }

    for (let i = 0; i < (stream.encoders?.length ?? 0); i++) {
      for (const key in properties) {
        if (stream.encoders?.[i]) {
          stream.encoders[i] = { ...stream.encoders[i], [key]: properties[key] } as Encoder;
        }
      }
    }

    setDebounceInterval(debounce ?? 100);
    setProject({
      ...project,
      stream: stream,
      encoder: stream.encoders?.find((x) => x.id === project.encoder?.id) || ({} as Encoder),
    });
  };

  const triggerUpdateStream = useCallback(() => {
    if (project.id === '') {
      setIsSavingStream(false);
      setIsAwaitingApiSaveToComplete(false);
      return;
    }

    setTimeout(() => {
      const request = {
        ...project.stream,
        ...project.encoder,
        streamId: project.stream.id
      };

      setIsAwaitingApiSaveToComplete(true);

      postStream(request).then((response) => {
        if (response.result) {
          onSave(response.result);
          setIsSavingStream(false);
        }
      }).catch((error) => {
        setIsSavingStream(false);
        setIsAwaitingApiSaveToComplete(false);
        console.log(`${JSON.stringify(error)}`);
      });
    }, debounceInterval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postStream]);

  useEffect(() => {
    if (!project
      || project?.id === '')
    {
      return;
    }

    if (!current
      || current?.id === '') {
      setCurrent(project);
      return;
    }

    if (isSavingStream) {
      return;
    }

    setIsSavingStream(true);
    setCurrent(project);
    triggerUpdateStream();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  return {
    project,
    setProject,
    updateEncoder,
    updateStream,
    isAwaitingApiSaveToComplete,
  };
};

export default useAutosaveProject;
